// Icons from the approved mockups (24-unit viewBox, 2-unit strokes).
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 24, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const HomeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 10.5L12 3l9 7.5" />
    <path d="M5 9v11h5v-6h4v6h5V9" />
  </Icon>
);

export const LibraryIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="4" width="5" height="16" rx="1" />
    <rect x="10.5" y="4" width="5" height="16" rx="1" />
    <path d="M17 5.5l3.2-.8 2.4 14.6-3.2.7z" />
  </Icon>
);

export const BuildIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </Icon>
);

export const SettingsIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </Icon>
);

export const BackIcon = (p: IconProps) => (
  <Icon size={22} {...p}>
    <path d="M15 18l-6-6 6-6" />
  </Icon>
);

export const SearchIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4-4" />
  </Icon>
);

export const ShieldIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6l8-3z" />
  </Icon>
);

export const InboxIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 13h5l2 3h4l2-3h5" />
    <path d="M5 5h14l2 8v6H3v-6z" />
  </Icon>
);

// Library and Home (mockups 05 and 06).

export const ChevronDownIcon = (p: IconProps) => (
  <Icon size={14} strokeWidth={2.2} {...p}>
    <path d="M6 9l6 6 6-6" />
  </Icon>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Icon size={18} {...p}>
    <path d="M9 6l6 6-6 6" />
  </Icon>
);

/** The favourite star: an outline, filled when `filled`. */
export const StarIcon = ({ filled = false, ...p }: IconProps & { filled?: boolean }) => (
  <Icon size={20} strokeWidth={1.8} fill={filled ? 'currentColor' : 'none'} {...p}>
    <path d="M12 3.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.8l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />
  </Icon>
);

/** In progress tile: a play button in a circle. */
export const PlayCircleIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 8.5v7l5.5-3.5z" fill="currentColor" stroke="none" />
  </Icon>
);

/** Generate tile: two sparkles. */
export const SparkleIcon = (p: IconProps) => (
  <Icon strokeWidth={1.8} {...p}>
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </Icon>
);

export const PersonIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </Icon>
);

/** The Play button's triangle. */
export const PlayTriangleIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5v14l11-7z" fill="currentColor" />
  </svg>
);

export const CloseIcon = (p: IconProps) => (
  <Icon size={20} {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Icon>
);

export const TickIcon = (p: IconProps) => (
  <Icon size={18} strokeWidth={2.2} {...p}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Icon>
);

// Status marks (16-unit viewBox), as on the Home and Library mockups.

/** Solved: a white tick on the Solved green. */
export const SolvedMark = ({ size = 12, ring = false }: { size?: number; ring?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
    <circle
      cx="8"
      cy="8"
      r={ring ? 7.5 : 7}
      className="fill-solved"
      stroke={ring ? 'var(--color-surface)' : undefined}
      strokeWidth={ring ? 1 : undefined}
    />
    <path
      d="M4.8 8.2l2.1 2.1 4.3-4.4"
      fill="none"
      stroke="var(--color-surface)"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** In progress: a half-filled accent circle. */
export const InProgressMark = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
    <circle cx="8" cy="8" r="6" fill="none" className="stroke-accent" strokeWidth="1.6" />
    <path d="M8 2a6 6 0 0 1 0 12z" className="fill-accent" />
  </svg>
);

/** Not started: an empty grey ring. */
export const NotStartedMark = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
    <circle cx="8" cy="8" r="6" fill="none" className="stroke-icon-soft" strokeWidth="1.6" />
  </svg>
);
